import { useState, useEffect, useRef, useCallback } from "react";

// ── CONFIGURE ─────────────────────────────────────────────────────────────────
const EDGE_FUNCTION_URL = process.env.REACT_APP_EDGE_FUNCTION_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;


// ── DATA MASKING ──────────────────────────────────────────────────────────────
function maskCompanyData(profile, hedges, receivables, payables, lcRecords , bcRecords ) {
  const parseAmt = (val) =>
    parseFloat((val || "").toString().replace(/[^0-9.]/g, "")) || 0;

  const today = new Date();
  const in30  = new Date(today); in30.setDate(today.getDate() + 30);

  const maskedProfile = profile
    ? {
        industry:      profile.industry      || "Manufacturing",
        base_currency: profile.base_currency || "INR",
        revenue_band:  profile.revenue_band  || "mid-market",
        region:        profile.region        || "India",
      }
    : { industry: "Unknown", base_currency: "INR", revenue_band: "mid-market", region: "India" };

  const summarize = (arr) => {
    const byCurrency = {};
    arr.forEach((item) => {
      const ccy = item.currency || "INR";
      if (!byCurrency[ccy]) byCurrency[ccy] = { count: 0, total: 0 };
      byCurrency[ccy].count++;
      byCurrency[ccy].total += parseAmt(item.amount || item.notional_amount_num);
    });
    return byCurrency;
  };

  const activeRec   = (receivables || []).filter((r) => r.status !== "Settled");
  const activePay   = (payables    || []).filter((p) => p.status !== "Settled");
  const totalRec    = activeRec.reduce((s, r) => s + parseAmt(r.amount), 0);
  const totalPay    = activePay.reduce((s, p) => s + parseAmt(p.amount), 0);
  const totalHedged = (hedges || []).reduce((s, h) => s + (parseFloat(h.notional_amount_num) || 0), 0);
  const hedgeRatio  = totalRec + totalPay > 0
    ? ((totalHedged / (totalRec + totalPay)) * 100).toFixed(1) : 0;

  const maturingSoon = (hedges || []).filter((h) => {
    const d = new Date(h.maturity_date || h.expiry_date);
    return d >= today && d <= in30;
  }).length;

  const activeLCs = (lcRecords || []).filter(r => r.status !== 'Settled' && r.status !== 'Converted to BC');
  const activeBCs = (bcRecords || []).filter(r => r.status !== 'Settled');
  const totalLC   = activeLCs.reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0), 0);
  const totalBC   = activeBCs.reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0), 0);
  const unhedgedLCs = activeLCs.filter(r => r.hedge_status === 'Unhedged').length;
  const lcMaturingSoon = activeLCs.filter(r => {
    const effDate = r.lc_type === 'Usance' && r.usance_period_days
      ? (() => { const d = new Date(r.maturity_date + 'T12:00:00'); d.setDate(d.getDate() + parseInt(r.usance_period_days)); return d; })()
      : new Date(r.maturity_date + 'T12:00:00');
    return effDate >= today && effDate <= in30;
  }).length;
  const bcRepayingSoon = activeBCs.filter(r => {
    const d = new Date(r.repayment_date + 'T12:00:00');
    return d >= today && d <= in30;
  }).length;

  return {
    profile: maskedProfile,
    fx_exposure: {
      total_receivables: totalRec,
      total_payables:    totalPay,
      net_exposure:      totalRec - totalPay,
    },
    hedging: {
      total_hedged:           totalHedged,
      hedge_ratio_pct:        hedgeRatio,
      contracts_maturing_30d: maturingSoon,
      hedge_count:            (hedges || []).length,
    },
    receivables_by_currency: summarize(activeRec),
    payables_by_currency:    summarize(activePay),
    overdue_receivables:     (receivables || []).filter((r) => r.status === "Overdue").length,
    import_financing: {
      total_lc_amount:    totalLC,
      total_bc_amount:    totalBC,
      active_lcs:         activeLCs.length,
      active_bcs:         activeBCs.length,
      unhedged_lcs:       unhedgedLCs,
      lc_maturing_30d:    lcMaturingSoon,
      bc_repaying_30d:    bcRepayingSoon,
      avg_bc_all_in_rate: activeBCs.length
        ? (activeBCs.reduce((s, b) => s + (parseFloat(b.all_in_rate) || 0), 0) / activeBCs.length).toFixed(2)
        : null,
    },
  };
}

// ── CFO ONBOARDING INSIGHTS (AI) ─────────────────────────────────────────────
async function fetchCFOInsights(profile, signal) {
  const debtProfile       = profile?.debt_profile       || [];
  const investmentProfile = profile?.investment_profile || [];
  const financials        = profile?.financials         || [];

  const avgDebtRate = debtProfile.length > 0
    ? (debtProfile.reduce((s, d) => s + (parseFloat(d.interest_rate) || 0), 0) / debtProfile.length).toFixed(2)
    : null;

  const totalDebt = debtProfile.reduce((s, d) => s + (parseFloat(d.loan_amount) || 0), 0);
  const totalInv  = investmentProfile.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  const systemPrompt = `You are a senior CFO advisor and treasury expert from Great Britain specialising in Indian corporates.
Based on the company's onboarding data, generate 4 high-value strategic insights a CFO would find genuinely useful.

CRITICAL DATA INTERPRETATION RULES — strictly follow these:
- "nav" on a Mutual Fund = Net Asset Value, the price per unit in ₹. It is NOT a rate of return. Never use NAV as a yield or return percentage.
- "stock_price" on a Stock = market price per share in ₹. It is NOT a return or yield.
- "rate" on debt instruments (Term Deposit, FD, NCD, GSec, T-Bill, CP) = annual interest rate in %. This is the only field that represents a return.
- Never compare NAV or stock_price against interest rates or benchmark yields.
- For Mutual Funds and Stocks, do not compute or state a return unless explicit 1Y/3Y return data is provided separately.

Return ONLY valid JSON — no markdown — with this structure:
{
  "insights": [
    {
      "title": "max 8 words",
      "category": "funding or benchmark_rates or working_capital or fx_strategy or investment or refinancing",
      "severity": "info or warning or critical",
      "insight": "2-3 sentences with specific numbers from the data. Be precise and actionable.",
      "action": "one specific CFO action max 15 words",
      "market_context": "1 sentence on current Indian market context"
    }
  ]
}
Focus on: funding trends for their sector, benchmark rate comparisons for their credit rating, working capital optimisation, FX hedging strategy, investment yield benchmarking, debt refinancing opportunities.`;

  const userContent = `Company profile:
- Industry: ${profile?.industry || "Manufacturing"}
- Country: ${profile?.country || "India"}
- Total Imports INR: ${profile?.total_imports_inr || 0}
- Total Exports INR: ${profile?.total_exports_inr || 0}
- Total Imports USD: ${profile?.total_imports_usd || 0}
- Total Exports USD: ${profile?.total_exports_usd || 0}

Debt profile (${debtProfile.length} facilities):
${debtProfile.map(d => `- ${d.debt_type} from ${d.provider}: ${d.loan_amount} ${d.currency} at ${d.interest_rate}% (rating: ${d.rating} by ${d.rating_provider}, benchmark: ${d.market_benchmark}%)`).join("\n")}

Investment profile (${investmentProfile.length} instruments):
${investmentProfile.map(i => {
  if (i.instrument_type === "Mutual Fund") {
    return `- Mutual Fund at ${i.placed_with}: ₹${i.amount} | NAV (unit price in ₹, NOT a return): ₹${i.nav || "N/A"} | Units: ${i.quantity || "N/A"} | IMPORTANT: Do not interpret NAV as a yield or return percentage`;
  }
  if (i.instrument_type === "Stock") {
    return `- Stock at ${i.placed_with}: ₹${i.amount} | Stock price (market price per share in ₹, NOT a return): ₹${i.stock_price || "N/A"} | Shares: ${i.quantity || "N/A"} | IMPORTANT: Do not interpret stock price as a yield`;
  }
  return `- ${i.instrument_type} at ${i.placed_with}: ₹${i.amount} | Interest rate p.a.: ${i.rate || "N/A"}%`;
}).join("\n")}

Financial summary:
${financials.map(f => `- ${f.year}: Sales ${f.sales}, EBIT ${f.ebit}, P&L ${f.pnl}`).join("\n")}

Average debt rate: ${avgDebtRate || "N/A"}%
Total debt: ${totalDebt.toLocaleString()}
Total investments: ${totalInv.toLocaleString()}

Generate 4 strategic CFO insights based on this data.`;

  const res = await fetch(EDGE_FUNCTION_URL, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      system:     systemPrompt,
      max_tokens: 1000,
      messages:   [{ role: "user", content: userContent }],
    }),
  });

  if (!res.ok) throw new Error(`Edge function error ${res.status}`);
  const data  = await res.json();
  const text  = (data.content || []).map((b) => b.text || "").join("");
  const clean = text.replace(/```json|```/g, "").trim();
  const parsed = JSON.parse(clean);
  return parsed.insights || [];
}

// ── CFO RULE-BASED FALLBACK ───────────────────────────────────────────────────
function generateCFOFallbackInsights(profile) {
  const insights      = [];
  const debtProfile   = profile?.debt_profile       || [];
  const invProfile    = profile?.investment_profile || [];
  const industry      = profile?.industry           || "Manufacturing";
  const importsINR    = profile?.total_imports_inr  || 0;
  const exportsINR    = profile?.total_exports_inr  || 0;

  // 1. Benchmark rate comparison
  if (debtProfile.length > 0) {
    const aboveMarket = debtProfile.filter(d =>
      parseFloat(d.interest_rate) > parseFloat(d.market_benchmark || 9.5)
    );
    if (aboveMarket.length > 0) {
      const avgSpread = (aboveMarket.reduce((s, d) =>
        s + parseFloat(d.interest_rate) - parseFloat(d.market_benchmark || 9.5), 0
      ) / aboveMarket.length).toFixed(2);
      insights.push({
        title:          `${aboveMarket.length} Facilities Above Market Benchmark`,
        category:       "benchmark_rates",
        severity:       "warning",
        insight:        `${aboveMarket.length} of your ${debtProfile.length} debt facilities are priced above market benchmark by an average of ${avgSpread}%. With ${debtProfile.filter(d => d.rating === "AAA" || d.rating === "AA+" || d.rating === "AA").length > 0 ? "strong" : "investment grade"} credit ratings, you may qualify for better pricing on renewal.`,
        action:         "Initiate rate renegotiation with lenders at next review date",
        market_context: "RBI repo rate at 6.5% — banks are competitive for well-rated corporates.",
        isCFO:          true,
      });
    }
  }

  // 2. FX exposure vs hedging strategy
  if (importsINR > 0 || exportsINR > 0) {
    const netExposure = exportsINR - importsINR;
    insights.push({
      title:          netExposure > 0 ? "Net Exporter — INR Depreciation Beneficial" : "Net Importer — Consider Forward Covers",
      category:       "fx_strategy",
      severity:       netExposure < 0 ? "warning" : "info",
      insight:        `Your annual FX exposure is ₹${Math.abs(importsINR + exportsINR).toLocaleString()} (Imports: ₹${importsINR.toLocaleString()}, Exports: ₹${exportsINR.toLocaleString()}). ${netExposure < 0 ? "As a net importer, INR depreciation increases your costs — consider systematic forward covers for 50-60% of import payables." : "As a net exporter, INR appreciation reduces your realisation — consider option strategies to protect downside."}`,
      action:         netExposure < 0 ? "Set up 3-6 month rolling forward covers for import payments" : "Evaluate export receivable hedging with participating forwards",
      isCFO:          true,
    });
  }

  // 3. Investment yield benchmarking
  if (invProfile.length > 0) {
    const debtInv = invProfile.filter(i => i.instrument_type !== "Mutual Fund" && i.instrument_type !== "Stock");
const avgRate = debtInv.length > 0
  ? debtInv.reduce((s, i) => s + parseFloat(i.rate || 0), 0) / debtInv.length
  : 0;
const fdCount = debtInv.filter(i => i.instrument_type === "Term Deposit" || i.instrument_type === "Certificate of Deposit").length;
    
  insights.push({
  title:          `Investment Yield at ${avgRate.toFixed(2)}% — Optimise Portfolio`,
  category:       "investment",
  severity:       avgRate < 7 ? "warning" : "info",
  insight:        `Your ${debtInv.length} debt investment instruments average ${avgRate.toFixed(2)}% p.a. ${invProfile.length - debtInv.length > 0 ? `(excludes ${invProfile.length - debtInv.length} equity/MF holdings which are valued at NAV, not yield). ` : ""}${fdCount > 0 ? `${fdCount} fixed deposits could be diversified into higher-yielding NCDs or T-Bills for improved returns.` : "Consider diversifying into corporate bonds or government securities for better risk-adjusted yields."} Current market offers 7.5-8.5% on AA-rated NCDs.`,
  action:         "Review investment mix — add NCDs or T-Bills for higher yield",
  market_context: "Short-term rates elevated — lock in yields before potential RBI rate cuts.",
  isCFO:          true,
});
  }

  // 4. Working capital / debt refinancing
  if (debtProfile.length > 0) {
    const shortTerm = debtProfile.filter(d =>
      d.debt_type === "Working Capital" || d.debt_type === "Short Term Loan" || d.debt_type === "Overdraft"
    );
    const totalShortTerm = shortTerm.reduce((s, d) => s + (parseFloat(d.loan_amount) || 0), 0);
    if (totalShortTerm > 0) {
      insights.push({
        title:          "Working Capital Refinancing Opportunity",
        category:       "refinancing",
        severity:       "info",
        insight:        `Your short-term working capital facilities total ${totalShortTerm.toLocaleString()} across ${shortTerm.length} instruments. In the current rate environment, consolidating these into a longer-tenure facility with a top-rated bank could reduce blended cost by 50-100 bps and improve working capital predictability.`,
        action:         "Request competitive proposals from 2-3 banks for WC facility consolidation",
        market_context: `${industry} sector working capital rates currently 8-10% — well-rated companies can negotiate at lower end.`,
        isCFO:          true,
      });
    }
  }

  // 5. Funding trend
  insights.push({
    title:          `${industry} Sector Funding Trends — Q1 2026`,
    category:       "funding",
    severity:       "info",
    insight:        `The ${industry} sector is seeing increased lender appetite with competitive pricing for investment-grade borrowers. Bank credit growth remains strong at 13-15% YoY. ESG-linked financing is gaining traction — ${industry} companies with strong governance profiles are accessing 25-50 bps cheaper facilities through green bonds.`,
    action:         "Explore ESG-linked financing for next debt issuance",
    market_context: "RBI's priority sector push benefits ${industry} borrowers — check eligibility for MSME or export refinance schemes.",
    isCFO:          true,
  });

  return insights.slice(0, 4);
}

// ── AI INSIGHT FETCH (hedge-based) ────────────────────────────────────────────
async function fetchTreasuryInsight(maskedData, existingTitles, signal) {
  const avoidLine = existingTitles.length > 0
    ? `Avoid repeating insights similar to: ${existingTitles.join("; ")}` : "";

  const systemPrompt = `You are a senior treasury analyst specialising in Indian corporates and Asia-Pacific FX markets.
Produce ONE concise actionable treasury insight for a treasury manager.
Return ONLY valid JSON — no markdown fences, no explanation — with exactly these keys:
{
  "title": "max 8 words",
  "category": "fx_risk or hedging or liquidity or market or counterparty or cash_flow",
  "severity": "info or warning or critical",
  "insight": "2-3 sentences with concrete recommendation. Reference USD/INR, RBI, or rate moves where relevant.",
  "action": "one specific action max 15 words",
  "market_context": "1 sentence on current INR or relevant FX market backdrop"
}
${avoidLine}`;

  const res = await fetch(EDGE_FUNCTION_URL, {
    method: "POST", signal,
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
    body: JSON.stringify({
      system: systemPrompt, max_tokens: 600,
      messages: [{ role: "user", content: `Company treasury data (anonymized):\n${JSON.stringify(maskedData, null, 2)}\n\nGenerate a fresh specific insight.` }],
    }),
  });
  if (!res.ok) throw new Error(`Edge function error ${res.status}`);
  const data  = await res.json();
  const text  = (data.content || []).map((b) => b.text || "").join("");
  const clean = text.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

// ── REPORT GENERATION ─────────────────────────────────────────────────────────
async function generateReport(maskedData, insights, signal) {
  const systemPrompt = `You are a senior treasury analyst writing an executive treasury report for an Indian corporate.
Return ONLY valid JSON — no markdown — with this exact structure:
{
  "executive_summary": "2-3 sentences",
  "market_section": {
    "heading": "Global FX and Market Overview",
    "content": "3-4 paragraphs",
    "key_rates": [
      {"pair":"USD/INR","trend":"bullish or bearish or neutral","note":"brief"},
      {"pair":"EUR/USD","trend":"bullish or bearish or neutral","note":"brief"},
      {"pair":"GBP/USD","trend":"bullish or bearish or neutral","note":"brief"},
      {"pair":"USD/JPY","trend":"bullish or bearish or neutral","note":"brief"}
    ]
  },
  "company_section": {
    "heading": "Treasury Position and Recommendations",
    "content": "3-4 paragraphs",
    "priority_actions": [
      {"rank":1,"action":"description","deadline":"timeframe","impact":"high or medium or low"},
      {"rank":2,"action":"description","deadline":"timeframe","impact":"high or medium or low"},
      {"rank":3,"action":"description","deadline":"timeframe","impact":"high or medium or low"}
    ]
  },
  "risk_matrix": [
    {"risk":"name","likelihood":"high or medium or low","impact":"high or medium or low","mitigation":"brief"}
  ],
  "outlook": "1-2 sentences"
}`;

  const res = await fetch(EDGE_FUNCTION_URL, {
    method: "POST", signal,
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
    body: JSON.stringify({
      system: systemPrompt, max_tokens: 1000,
      messages: [{ role: "user", content: `Treasury data: ${JSON.stringify(maskedData, null, 2)}\nInsights: ${JSON.stringify(insights.map(i => ({ title: i.title, insight: i.insight, action: i.action })))}\nGenerate the report.` }],
    }),
  });
  if (!res.ok) throw new Error(`Edge function error ${res.status}`);
  const data  = await res.json();
  const text  = (data.content || []).map((b) => b.text || "").join("");
  const clean = text.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

// ── RULE-BASED FALLBACK (hedge insights) ─────────────────────────────────────
function generateRuleBasedInsights(maskedData) {
  const insights = [];
  const { fx_exposure, hedging, receivables_by_currency, payables_by_currency, overdue_receivables, profile } = maskedData;
  const ratio = parseFloat(hedging.hedge_ratio_pct);

  if (ratio < 40) {
    insights.push({ title: "Hedging Coverage Below Benchmark", category: "hedging", severity: "critical",
      insight: `Current hedging coverage is ${ratio.toFixed(1)}% — well below the industry benchmark of 50-60% for ${profile.industry} sector. Significant FX exposure remains unhedged.`,
      action: "Increase hedge positions to reach 50% coverage",
      market_context: "USD/INR volatility remains elevated — low hedge ratios amplify downside risk.", isRuleBased: true });
  } else if (ratio < 55) {
    insights.push({ title: "Hedging Coverage Approaching Benchmark", category: "hedging", severity: "warning",
      insight: `Current hedging coverage is ${ratio.toFixed(1)}%. Industry benchmark is 50-60% for ${profile.industry} sector. Consider topping up to reduce residual FX risk.`,
      action: "Top up hedge positions to reach 55-60% coverage",
      market_context: "Maintaining adequate hedge ratios is critical given current INR volatility.", isRuleBased: true });
  } else {
    insights.push({ title: "Hedging Coverage Within Benchmark", category: "hedging", severity: "info",
      insight: `Hedging coverage is ${ratio.toFixed(1)}% — within the industry benchmark of 50-60% for ${profile.industry} sector.`,
      action: "Monitor hedge ratio as new receivables or payables are added",
      market_context: "Sustaining hedge coverage above 50% protects against INR depreciation.", isRuleBased: true });
  }

  const net = fx_exposure.net_exposure;
  if (Math.abs(net) > 0) {
    insights.push({ title: net < 0 ? "Net Short USD Position Detected" : "Net Long USD Position Detected",
      category: "fx_risk", severity: Math.abs(net) > 200000 ? "critical" : "warning",
      insight: `Your net FX exposure is ${Math.abs(net).toLocaleString()} ${profile.base_currency} ${net < 0 ? "short" : "long"}.`,
      action: `Review ${net < 0 ? "payables" : "receivables"} timing to reduce net exposure`,
      market_context: "USD/INR direction is a key driver of your net position P&L impact.", isRuleBased: true });
  }

  if (hedging.contracts_maturing_30d > 0) {
    insights.push({ title: `${hedging.contracts_maturing_30d} Contract(s) Maturing Within 30 Days`,
      category: "hedging", severity: "warning",
      insight: `You have ${hedging.contracts_maturing_30d} hedge contract(s) maturing in the next 30 days.`,
      action: "Contact your bank to discuss rollover or settlement options",
      market_context: "Rolling at current USD/INR levels may be advantageous.", isRuleBased: true });
  }

  if (overdue_receivables > 0) {
    insights.push({ title: `${overdue_receivables} Overdue Receivable(s)`,
      category: "cash_flow", severity: "critical",
      insight: `You have ${overdue_receivables} overdue receivable(s). Delayed collections increase your net FX exposure.`,
      action: "Escalate overdue receivables to collections team immediately",
      market_context: "Unresolved receivables extend your open FX exposure period.", isRuleBased: true });
  }
  // ADD at the end before return:
const lcBC = maskedData.import_financing;
if (lcBC && lcBC.lc_maturing_30d > 0) {
  insights.push({
    title: `${lcBC.lc_maturing_30d} LC${lcBC.lc_maturing_30d > 1 ? 's' : ''} Maturing in 30 Days`,
    category: "cash_flow", severity: "warning",
    insight: `${lcBC.lc_maturing_30d} Letter${lcBC.lc_maturing_30d > 1 ? 's' : ''} of Credit maturing within 30 days with total import exposure of ${lcBC.total_lc_amount.toLocaleString()} FCY. Consider settlement or conversion to Buyers Credit to extend tenor.`,
    action: "Review LC maturity — Settle or convert to Buyers Credit",
    market_context: "Buyers Credit rates currently linked to SOFR — evaluate cost vs direct settlement.",
    isRuleBased: true,
  });
}
if (lcBC && lcBC.unhedged_lcs > 0) {
  insights.push({
    title: `${lcBC.unhedged_lcs} Unhedged LC${lcBC.unhedged_lcs > 1 ? 's' : ''} — Open FX Exposure`,
    category: "fx_risk", severity: lcBC.unhedged_lcs > 2 ? "critical" : "warning",
    insight: `${lcBC.unhedged_lcs} active LC${lcBC.unhedged_lcs > 1 ? 's' : ''} have no forward cover. These import payables carry open FX risk — any INR depreciation directly increases your cost.`,
    action: "Add forward cover for unhedged LCs immediately",
    market_context: "USD/INR volatility warrants systematic hedging of import LC payables.",
    isRuleBased: true,
  });
}
if (lcBC && lcBC.avg_bc_all_in_rate) {
  insights.push({
    title: `Buyers Credit All-in Rate at ${lcBC.avg_bc_all_in_rate}%`,
    category: "funding", severity: "info",
    insight: `Your ${lcBC.active_bcs} active Buyers Credit facilit${lcBC.active_bcs > 1 ? 'ies average' : 'y is at'} ${lcBC.avg_bc_all_in_rate}% all-in (benchmark + spread). ${lcBC.bc_repaying_30d > 0 ? `${lcBC.bc_repaying_30d} repayment${lcBC.bc_repaying_30d > 1 ? 's' : ''} due within 30 days — ensure FCY funds are arranged.` : 'Monitor benchmark rate movements for refinancing opportunities.'}`,
    action: lcBC.bc_repaying_30d > 0 ? "Arrange FCY funds for upcoming BC repayments" : "Review BC spread vs current SOFR/SONIA levels",
    market_context: "SOFR at 3.65% — total BC cost depends on lender spread above benchmark.",
    isRuleBased: true,
  });
}

  return insights.slice(0, 6);
}

function generateRuleBasedReport(maskedData) {
  const { profile, fx_exposure, hedging, overdue_receivables } = maskedData;
  const ratio = parseFloat(hedging.hedge_ratio_pct);
  const net   = fx_exposure.net_exposure;
  return {
    executive_summary: `${profile.industry} sector treasury position shows a hedge ratio of ${ratio}% against total FX exposure. Net exposure stands at ${Math.abs(net).toLocaleString()} ${profile.base_currency} ${net < 0 ? "short" : "long"}.`,
    market_section: {
      heading: "Global FX and Market Overview",
      content: "USD/INR remains sensitive to global risk sentiment and RBI intervention. RBI has been actively managing volatility. Interest rate differentials between India and the US continue to influence capital flows.",
      key_rates: [
        { pair: "USD/INR", trend: "neutral", note: "RBI managing volatility" },
        { pair: "EUR/USD", trend: "neutral", note: "Fed vs ECB divergence"  },
        { pair: "GBP/USD", trend: "neutral", note: "UK economic uncertainty"},
        { pair: "USD/JPY", trend: "bullish", note: "BOJ yield curve control" },
      ],
    },
    company_section: {
      heading: "Treasury Position and Recommendations",
      content: `Current hedge ratio of ${ratio}% ${ratio < 40 ? "is below benchmark" : "is within benchmark"}. ${hedging.contracts_maturing_30d > 0 ? `${hedging.contracts_maturing_30d} contract(s) maturing within 30 days.` : ""} ${overdue_receivables > 0 ? `${overdue_receivables} overdue receivable(s) need follow-up.` : ""}`,
      priority_actions: [
        { rank: 1, action: ratio < 50 ? "Increase hedge ratio to minimum 50%" : "Maintain current hedge ratio", deadline: "This week", impact: ratio < 40 ? "high" : "medium" },
        { rank: 2, action: hedging.contracts_maturing_30d > 0 ? "Roll over maturing hedge contracts" : "Review next quarter FX exposure", deadline: "This month", impact: "medium" },
        { rank: 3, action: overdue_receivables > 0 ? "Escalate overdue receivables" : "Monitor counterparty credit", deadline: "Immediately", impact: overdue_receivables > 0 ? "high" : "low" },
      ],
    },
    risk_matrix: [
      { risk: "FX Rate Movement",  likelihood: "high",   impact: net < 0 ? "high" : "medium", mitigation: "Increase hedge ratio"         },
      { risk: "Hedge Maturity Gap",likelihood: hedging.contracts_maturing_30d > 0 ? "high" : "low", impact: "medium", mitigation: "Plan rollovers in advance" },
      { risk: "Counterparty Credit",likelihood:"low",    impact: "high",   mitigation: "Diversify banking relationships"             },
      { risk: "Liquidity Mismatch",likelihood: overdue_receivables > 0 ? "medium" : "low", impact: "medium", mitigation: "Tighten receivables" },
    ],
    outlook: `INR expected to remain range-bound with RBI support. ${ratio < 50 ? "Increasing hedge coverage is the priority." : "Focus on rolling maturing contracts at favourable rates."}`,
    isRuleBased: true,
  };
}

// ── STYLE CONFIG ──────────────────────────────────────────────────────────────
const CAT = {
  fx_risk:       { label: "FX Risk",        bg: "rgba(249,115,22,0.15)",  color: "#fb923c" },
  hedging:       { label: "Hedging",         bg: "rgba(99,102,241,0.15)", color: "#818cf8" },
  liquidity:     { label: "Liquidity",       bg: "rgba(20,184,166,0.15)", color: "#2dd4bf" },
  market:        { label: "Market",          bg: "rgba(168,85,247,0.15)", color: "#c084fc" },
  counterparty:  { label: "Counterparty",    bg: "rgba(234,179,8,0.15)",  color: "#facc15" },
  cash_flow:     { label: "Cash Flow",       bg: "rgba(34,197,94,0.15)",  color: "#4ade80" },
  funding:       { label: "Funding",         bg: "rgba(59,130,246,0.15)", color: "#60a5fa" },
  benchmark_rates:{ label: "Benchmark Rates",bg: "rgba(234,179,8,0.15)", color: "#facc15" },
  working_capital:{ label: "Working Capital",bg: "rgba(20,184,166,0.15)",color: "#2dd4bf" },
  fx_strategy:   { label: "FX Strategy",    bg: "rgba(249,115,22,0.15)", color: "#fb923c" },
  investment:    { label: "Investment",      bg: "rgba(34,197,94,0.15)",  color: "#4ade80" },
  refinancing:   { label: "Refinancing",     bg: "rgba(99,102,241,0.15)",color: "#818cf8" },
};

const SEV  = { info: { dot: "#818cf8" }, warning: { dot: "#facc15" }, critical: { dot: "#f87171" } };
const TREND = { bullish: { symbol: "▲", color: "#4ade80" }, bearish: { symbol: "▼", color: "#f87171" }, neutral: { symbol: "→", color: "#9ca3af" } };

// ── INSIGHT CARD ──────────────────────────────────────────────────────────────
function InsightCard({ insight, isNew }) {
  const cat = CAT[insight.category] || CAT.fx_risk;
  const sev = SEV[insight.severity] || SEV.info;
  return (
    <div style={{
      background: "#0a0b0f", border: "1px solid #2a2d3a", borderRadius: 12,
      padding: "12px 14px",
      opacity: isNew ? 0 : 1, transform: isNew ? "translateY(6px)" : "none",
      transition: "opacity 0.4s ease, transform 0.4s ease",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: cat.bg, color: cat.color, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {cat.label}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: sev.dot, display: "inline-block" }} />
          {insight.severity}
        </span>
        {insight.isCFO && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "rgba(59,130,246,0.1)", color: "#60a5fa", fontWeight: 600, border: "1px solid rgba(59,130,246,0.2)" }}>
            👔 CFO Insight
          </span>
        )}
        {insight.isRuleBased && !insight.isCFO && (
          <span style={{ fontSize: 10, color: "#facc15", fontWeight: 600 }}>⚡</span>
        )}
        {isNew && (
          <span style={{ marginLeft: "auto", fontSize: 10, color: "#818cf8", fontWeight: 600 }}>NEW</span>
        )}
      </div>
      <p style={{ fontSize: 13, fontWeight: 600, color: "#f1f5f9", margin: "0 0 5px", lineHeight: 1.4 }}>{insight.title}</p>
      <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 8px", lineHeight: 1.6 }}>{insight.insight}</p>
      {insight.market_context && (
        <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 10px", borderLeft: "2px solid #2a2d3a", paddingLeft: 8, lineHeight: 1.5, fontStyle: "italic" }}>
          {insight.market_context}
        </p>
      )}
      <div style={{ fontSize: 11, color: "#818cf8", background: "rgba(99,102,241,0.1)", borderRadius: 4, padding: "3px 10px", display: "inline-block", fontWeight: 500, border: "1px solid rgba(99,102,241,0.2)" }}>
        → {insight.action}
      </div>
    </div>
  );
}

// ── REPORT VIEW ───────────────────────────────────────────────────────────────
function ReportView({ report, onClose }) {
  if (!report) return null;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>Treasury Report</h3>
          {report.isRuleBased
            ? <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(250,204,21,0.1)", color: "#facc15", fontWeight: 600, border: "1px solid rgba(250,204,21,0.2)" }}>⚡ Rule-based</span>
            : <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(99,102,241,0.1)", color: "#818cf8", fontWeight: 600, border: "1px solid rgba(99,102,241,0.2)" }}>✦ AI powered</span>}
        </div>
        <button onClick={onClose} style={{ border: "1px solid #2a2d3a", borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontSize: 12, background: "transparent", color: "#9ca3af" }}>← Back</button>
      </div>
      <div style={{ background: "#0a0b0f", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
        <p style={{ fontSize: 12, lineHeight: 1.65, margin: 0, color: "#94a3b8" }}>{report.executive_summary}</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div style={{ background: "#0a0b0f", border: "1px solid #2a2d3a", borderRadius: 10, padding: "12px 14px" }}>
          <p style={{ fontSize: 10, color: "#818cf8", fontWeight: 600, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Market</p>
          <p style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6, margin: "0 0 10px" }}>{report.market_section?.content}</p>
          <div style={{ borderTop: "1px solid #1e2130", paddingTop: 8 }}>
            {(report.market_section?.key_rates || []).map((r, i) => {
              const t = TREND[r.trend] || TREND.neutral;
              return (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "center", fontSize: 12 }}>
                  <span style={{ fontWeight: 600, minWidth: 68, color: "#e2e8f0", fontSize: 11 }}>{r.pair}</span>
                  <span style={{ color: t.color, fontSize: 11 }}>{t.symbol} {r.trend}</span>
                  <span style={{ color: "#64748b", fontSize: 11 }}>{r.note}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ background: "#0a0b0f", border: "1px solid #2a2d3a", borderRadius: 10, padding: "12px 14px" }}>
          <p style={{ fontSize: 10, color: "#2dd4bf", fontWeight: 600, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Company</p>
          <p style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6, margin: "0 0 10px" }}>{report.company_section?.content}</p>
          <div style={{ borderTop: "1px solid #1e2130", paddingTop: 8 }}>
            {(report.company_section?.priority_actions || []).map((a, i) => {
              const ic = { high: "#f87171", medium: "#facc15", low: "#4ade80" }[a.impact] || "#9ca3af";
              return (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
                  <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#1a1d26", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#9ca3af", flexShrink: 0 }}>{a.rank}</span>
                  <div>
                    <p style={{ margin: "0 0 1px", fontSize: 12, color: "#e2e8f0", lineHeight: 1.4 }}>{a.action}</p>
                    <span style={{ fontSize: 10, color: "#64748b" }}>{a.deadline} · </span>
                    <span style={{ fontSize: 10, color: ic, fontWeight: 600 }}>{a.impact} impact</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {report.outlook && (
        <div style={{ borderLeft: "3px solid #4f46e5", paddingLeft: 12 }}>
          <p style={{ fontSize: 10, color: "#818cf8", fontWeight: 600, margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Outlook</p>
          <p style={{ fontSize: 12, color: "#94a3b8", margin: 0, lineHeight: 1.6 }}>{report.outlook}</p>
        </div>
      )}
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────
export default function AIInsightsPage({ onBack, profile, hedges = [], receivables = [], payables = [], companyId, debtPositions = [] ,lcRecords = [], bcRecords = []}) {
  const [hedgeInsights, setHedgeInsights]   = useState([]);
  const [cfoInsights,   setCfoInsights]     = useState([]);
  const [activeView,    setActiveView]      = useState("cfo"); // "cfo" or "hedge"
  const [loading,       setLoading]         = useState(false);
  const [cfoLoading,    setCfoLoading]      = useState(false);
  const [newIdx,        setNewIdx]          = useState(null);
  const [error,         setError]           = useState(null);
  const [isRuleBased,   setIsRuleBased]     = useState(false);
  const [view,          setView]            = useState("insights");
  const [report,        setReport]          = useState(null);
  const [reportLoading, setReportLoading]   = useState(false);
  const abortRef      = useRef(null);
  const hasFetchedRef = useRef(false);
  const toggleRef     = useRef(null);

  const hasHedgeData = (hedges || []).length > 0;

  // ── Auto-toggle when hedge data exists ──────────────────────────────────
  useEffect(() => {
    if (!hasHedgeData) {
      setActiveView("cfo");
      return;
    }
    // Toggle every 8 seconds
    toggleRef.current = setInterval(() => {
      setActiveView(v => v === "cfo" ? "hedge" : "cfo");
    }, 8000);
    return () => clearInterval(toggleRef.current);
  }, [hasHedgeData]);

  // ── Load CFO insights ────────────────────────────────────────────────────
  const loadCFOInsights = useCallback(async () => {
    if (!profile) return;
    setCfoLoading(true);
    try {
      const insights = await fetchCFOInsights(profile, abortRef.current?.signal);
      insights.forEach(i => i.isCFO = true);
      setCfoInsights(insights);
    } catch (e) {
      if (e.name === "AbortError") return;
      const fallback = generateCFOFallbackInsights(profile);
      setCfoInsights(fallback);
    } finally {
      setCfoLoading(false);
    }
  }, [profile]);

  // ── Load hedge insights ──────────────────────────────────────────────────
  const doFetch = useCallback(async (masked) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    try {
      const existingTitles = hedgeInsights.map(i => i.title);
      const insight = await fetchTreasuryInsight(masked, existingTitles, abortRef.current.signal);
      insight.isRuleBased = false;
      setHedgeInsights(prev => {
        const updated = [insight, ...prev].slice(0, 6);
        return updated;
      });
      setIsRuleBased(false);
      setNewIdx(0);
      setTimeout(() => setNewIdx(null), 600);
    } catch (e) {
      if (e.name === "AbortError") return;
      const fallback = generateRuleBasedInsights(masked);
      if (fallback.length > 0) {
        setHedgeInsights(fallback);
        setIsRuleBased(true);
      } else {
        setError("Unable to generate insights at this time.");
      }
    } finally {
      setLoading(false);
    }
  }, [hedgeInsights, loading]);

  const handleReport = async () => {
    setReportLoading(true);
    setError(null);
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    try {
      const masked = maskCompanyData(profile, hedges, receivables, payables,lcRecords, bcRecords);
      const r = await generateReport(masked, hedgeInsights.length > 0 ? hedgeInsights : cfoInsights, abortRef.current.signal);
      r.isRuleBased = false;
      setReport(r); setView("report");
    } catch (e) {
      if (e.name === "AbortError") return;
      const masked       = maskCompanyData(profile, hedges, receivables, payables,lcRecords = [], bcRecords = [] );
      const fallbackReport = generateRuleBasedReport(masked);
      setReport(fallbackReport); setView("report");
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (!profile || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadCFOInsights();
    if (hasHedgeData) {
      doFetch(maskCompanyData(profile, hedges, receivables, payables,lcRecords, bcRecords));
    }
  }, [profile]); // eslint-disable-line

  if (!profile) {
    return (
      <div style={{ textAlign: "center", padding: "24px 0" }}>
        <div style={{ width: 18, height: 18, border: "2px solid #4f46e5", borderTopColor: "transparent", borderRadius: "50%", animation: "ntspin 0.8s linear infinite", margin: "0 auto 8px" }} />
        <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>Loading financial data...</p>
        <style>{`@keyframes ntspin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (view === "report" && report) return <ReportView report={report} onClose={() => setView("insights")} />;

  const displayInsights = activeView === "cfo" ? cfoInsights : hedgeInsights;
  const isDisplayLoading = activeView === "cfo" ? cfoLoading : loading;

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>

        {/* Mode toggle tabs — only show when hedge data exists */}
        {hasHedgeData && (
          <div style={{ display: "flex", gap: 4, background: "#0a0b0f", borderRadius: 8, padding: 3, border: "1px solid #2a2d3a" }}>
            {[
              { id: "cfo",   label: "👔 CFO Insights"    },
              { id: "hedge", label: "📊 Hedge Insights"  },
            ].map(tab => (
              <button key={tab.id} onClick={() => { setActiveView(tab.id); clearInterval(toggleRef.current); }}
                style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  border: "none", cursor: "pointer",
                  background: activeView === tab.id ? "#6366f1" : "transparent",
                  color:      activeView === tab.id ? "#fff"    : "#64748b",
                  transition: "all 0.2s",
                }}>{tab.label}</button>
            ))}
          </div>
        )}

        {/* Mode indicator — shows clearly which logic is being used */}
<div style={{
  display: "flex", alignItems: "center", gap: 6,
  background: "#0a0b0f", borderRadius: 8, padding: "4px 10px",
  border: "1px solid #2a2d3a", fontSize: 11,
}}>
  {activeView === "cfo" ? (
    cfoInsights.length > 0 && cfoInsights[0].isCFO && !cfoInsights[0].isRuleBased ? (
      <span style={{ color: "#818cf8", fontWeight: 700 }}>✦ AI Powered</span>
    ) : cfoInsights.length > 0 && cfoInsights[0].title === "AI Insights Unavailable" ? (
      <span style={{ color: "#f87171", fontWeight: 700 }}>✗ AI Unavailable</span>
    ) : (
      <span style={{ color: "#facc15", fontWeight: 700 }}>⚡ Rule Based</span>
    )
  ) : (
    isRuleBased ? (
      <span style={{ color: "#facc15", fontWeight: 700 }}>⚡ Rule Based</span>
    ) : hedgeInsights.length > 0 ? (
      <span style={{ color: "#818cf8", fontWeight: 700 }}>✦ AI Powered</span>
    ) : (
      <span style={{ color: "#64748b", fontWeight: 700 }}>— Loading</span>
    )
  )}
  <span style={{ color: "#374151" }}>|</span>
  <span style={{ color: "#64748b" }}>
    {activeView === "cfo" ? "👔 CFO Insights" : "📊 Hedge Insights"}
  </span>
</div>

        {/* Badges */}
        {!hasHedgeData && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(59,130,246,0.1)", color: "#60a5fa", fontWeight: 600, border: "1px solid rgba(59,130,246,0.2)" }}>
            👔 CFO Strategic Insights
          </span>
        )}
        {activeView === "hedge" && isRuleBased && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(250,204,21,0.1)", color: "#facc15", fontWeight: 600, border: "1px solid rgba(250,204,21,0.2)" }}>⚡ Rule-based</span>
        )}
        {activeView === "hedge" && !isRuleBased && hedgeInsights.length > 0 && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(99,102,241,0.1)", color: "#818cf8", fontWeight: 600, border: "1px solid rgba(99,102,241,0.2)" }}>✦ AI powered</span>
        )}

        {/* Refresh */}
        <button onClick={() => activeView === "cfo" ? loadCFOInsights() : doFetch(maskCompanyData(profile, hedges, receivables, payables,lcRecords, bcRecords))}
          disabled={isDisplayLoading}
          style={{ marginLeft: "auto", border: "1px solid #2a2d3a", borderRadius: 8, padding: "4px 12px", cursor: isDisplayLoading ? "not-allowed" : "pointer", fontSize: 11, background: "transparent", color: isDisplayLoading ? "#374151" : "#9ca3af" }}>
          {isDisplayLoading ? "Fetching…" : "↻ Refresh"}
        </button>

        {/* Report */}
        <button onClick={handleReport} disabled={reportLoading || (hedgeInsights.length === 0 && cfoInsights.length === 0)}
          style={{ border: "1px solid rgba(99,102,241,0.35)", borderRadius: 8, padding: "4px 12px", fontSize: 11, background: "rgba(99,102,241,0.1)", color: "#818cf8", fontWeight: 600, cursor: reportLoading ? "not-allowed" : "pointer" }}>
          {reportLoading ? "Building…" : "Generate report"}
        </button>
      </div>

      {/* Auto-toggle indicator */}
      {hasHedgeData && (
        <div style={{ fontSize: 10, color: "#374151", marginBottom: 8, textAlign: "right" }}>
          Auto-switching between CFO and Hedge insights every 8s
        </div>
      )}

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", color: "#fca5a5", borderRadius: 8, padding: "10px 12px", fontSize: 12, marginBottom: 10, border: "1px solid rgba(239,68,68,0.2)" }}>
          {error}
        </div>
      )}

      {/* Loading skeletons */}
      {isDisplayLoading && displayInsights.length === 0 && (
        <>
          {[1, 2].map(i => (
            <div key={i} style={{ background: "#0a0b0f", borderRadius: 12, height: 100, border: "1px solid #2a2d3a", marginBottom: 10, animation: "ntpulse 1.5s ease-in-out infinite" }} />
          ))}
          <style>{`@keyframes ntpulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
        </>
      )}

      {/* Insights */}
      {displayInsights.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {displayInsights.map((insight, i) => (
            <InsightCard key={`${insight.title}-${i}`} insight={insight} isNew={i === newIdx} />
          ))}
        </div>
      )}

      {!isDisplayLoading && displayInsights.length === 0 && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#374151", fontSize: 13 }}>
          Click ↻ Refresh to load insights
        </div>
      )}
    </div>
  );
}
